import {
  legacyParityEventNameSchema,
  productEventSchema,
  type DomainEventName,
  type LegacyParityEventName,
  type ProductEventName,
} from '@galapagos/product-analytics';
import type { AnalyticsProvider } from '../types';

export type AnalyticsEvidenceIdentity = 'anonymous' | 'identified';

export interface AnalyticsEvidenceEntry {
  id: string;
  event: ProductEventName | DomainEventName | LegacyParityEventName;
  marker: string;
  occurredAt: string;
}

export interface AnalyticsEvidenceSnapshot {
  identity: AnalyticsEvidenceIdentity;
  subjectId: string | null;
  markers: readonly string[];
  entries: readonly AnalyticsEvidenceEntry[];
  eventCount: number;
}

type Listener = () => void;

const MAX_EVIDENCE_ENTRIES = 1_000;
let identity: AnalyticsEvidenceIdentity = 'anonymous';
let subjectId: string | null = null;
let markers: string[] = [];
let entries: AnalyticsEvidenceEntry[] = [];
let eventCount = 0;
let currentSnapshot: AnalyticsEvidenceSnapshot = Object.freeze({
  identity,
  subjectId,
  markers: Object.freeze([]),
  entries: Object.freeze([]),
  eventCount,
});
const listeners = new Set<Listener>();

function notify(): void {
  currentSnapshot = Object.freeze({
    identity,
    subjectId,
    markers: Object.freeze([...markers]),
    entries: Object.freeze([...entries]),
    eventCount,
  });
  listeners.forEach((listener) => listener());
}

function normalized(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  return value.replaceAll('.', '-').replaceAll('/', '-').replaceAll('_', '-');
}

function markerFor(
  event: ProductEventName | DomainEventName | LegacyParityEventName,
  properties: Record<string, unknown>,
): string {
  // Legacy-parity events keep production's names; the entry screen is the one
  // property an e2e check needs to tell the first screens apart.
  if (legacyParityEventNameSchema.safeParse(event).success) {
    return ['legacy', normalized(event), normalized(properties.entry_screen)]
      .filter((part) => part !== undefined)
      .join('-');
  }
  switch (event) {
    case 'screen_viewed':
      return `view-${normalized(properties.screen_id)}`;
    case 'user_action':
      return `action-${normalized(properties.action_id)}`;
    case 'server_outcome':
      return `outcome-${normalized(properties.outcome_id)}-${normalized(properties.status)}`;
    case 'feature_flag_evaluated':
      return `flag-${normalized(properties.flag_key)}-${normalized(properties.variant_key)}`;
    case 'experiment_assigned':
      return `experiment-assigned-${normalized(properties.experiment_key)}-${normalized(properties.variant_key)}`;
    case 'experiment_exposed':
      return `experiment-exposed-${normalized(properties.experiment_key)}-${normalized(properties.variant_key)}`;
    case 'experiment_outcome':
      return `experiment-outcome-${normalized(properties.experiment_key)}-${normalized(properties.status)}`;
    default:
      return `domain-${normalized(event)}`;
  }
}

/**
 * Bounded, process-local evidence sink for explicitly enabled dev/E2E builds.
 * It retains canonical marker names and the canonical analytics subject ID so
 * engineers can reconcile a dev session with PostHog. It never retains free
 * text, answers, URLs, or arbitrary property values and cannot be enabled in
 * production.
 */
const evidenceProvider: AnalyticsProvider = {
  track(event, properties, options) {
    const parsed = productEventSchema.safeParse({ event, properties, options });
    if (!parsed.success) return;
    eventCount += 1;
    const marker = markerFor(parsed.data.event, parsed.data.properties);
    markers = [...markers, marker].slice(-MAX_EVIDENCE_ENTRIES);
    entries = [
      ...entries,
      {
        id: `evidence-${eventCount}`,
        event: parsed.data.event,
        marker,
        occurredAt: parsed.data.options?.timestamp ?? new Date().toISOString(),
      },
    ].slice(-MAX_EVIDENCE_ENTRIES);
    notify();
  },
  setUser(nextSubjectId) {
    identity = 'identified';
    subjectId = nextSubjectId;
    notify();
  },
  clearUser() {
    identity = 'anonymous';
    subjectId = null;
    notify();
  },
};

/** A deterministic throwing sink used only in explicitly enabled non-production E2E builds. */
export const analyticsFailureProbe: AnalyticsProvider = {
  track() {
    throw new Error('analytics failure probe');
  },
  setUser() {
    throw new Error('analytics failure probe');
  },
  clearUser() {
    throw new Error('analytics failure probe');
  },
};

export const analyticsEvidence = {
  provider: evidenceProvider,
  snapshot(): AnalyticsEvidenceSnapshot {
    return currentSnapshot;
  },
  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  reset(): void {
    identity = 'anonymous';
    subjectId = null;
    markers = [];
    entries = [];
    eventCount = 0;
    notify();
  },
};

export function analyticsEvidenceEnabled(raw: string | undefined, environment?: string): boolean {
  const normalizedEnvironment = environment?.trim().toLowerCase();
  return (
    raw?.trim().toLowerCase() === 'true' &&
    normalizedEnvironment !== 'prod' &&
    normalizedEnvironment !== 'production'
  );
}

export function analyticsSyntheticTrafficEnabled(
  raw: string | undefined,
  environment?: string,
): boolean {
  if (raw?.trim().toLowerCase() !== 'true') return false;
  const normalizedEnvironment = environment?.trim().toLowerCase();
  return (
    normalizedEnvironment === 'local' ||
    normalizedEnvironment === 'test' ||
    normalizedEnvironment === 'dev' ||
    normalizedEnvironment === 'development' ||
    normalizedEnvironment === 'preview' ||
    /^pr-\d+$/.test(normalizedEnvironment ?? '')
  );
}
